import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_33fe3333-b663-46d5-9a5a-213e797d3d01/artifacts/l3uhj6bk_logo-primary-2026-03-18.png';

export default function Header() {
  const { t, lang, toggleLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'about', href: '#about' },
    { key: 'services', href: '#services' },
    { key: 'process', href: '#process' },
    { key: 'gallery', href: '#gallery' },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/95 border-b border-[#C8C9C7] shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" onClick={() => scrollTo('#hero')} data-testid="header-logo" className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="AFRICAINOX"
              className="h-10 w-auto"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => scrollTo(item.href)}
                data-testid={`nav-${item.key}`}
                className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                  scrolled ? 'text-[#333F48] hover:text-[#003865]' : 'text-white/90 hover:text-white'
                }`}
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLang}
              data-testid="language-toggle"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-colors duration-200 ${
                scrolled
                  ? 'border-[#C8C9C7] text-[#333F48] hover:border-[#003865] hover:text-[#003865]'
                  : 'border-white/40 text-white/90 hover:border-white hover:text-white'
              }`}
            >
              <Globe size={14} />
              {lang === 'en' ? 'FR' : 'EN'}
            </button>
            <button
              onClick={() => scrollTo('#contact')}
              data-testid="nav-contact-btn"
              className="bg-[#003865] text-white px-6 py-2.5 text-sm font-semibold uppercase tracking-wide hover:bg-[#004B87] transition-colors duration-200"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {t('nav.contact')}
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-3 z-50 relative ${scrolled ? 'text-[#333F48]' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#C8C9C7] shadow-lg" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => scrollTo(item.href)}
                data-testid={`mobile-nav-${item.key}`}
                className="block w-full text-left text-sm font-medium text-[#333F48] hover:text-[#003865] uppercase tracking-wide py-2"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-[#C8C9C7]">
              <button
                onClick={toggleLang}
                data-testid="mobile-language-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-[#C8C9C7] text-[#333F48]"
              >
                <Globe size={14} />
                {lang === 'en' ? 'FR' : 'EN'}
              </button>
              <button
                onClick={() => scrollTo('#contact')}
                data-testid="mobile-contact-btn"
                className="bg-[#003865] text-white px-6 py-2.5 text-sm font-semibold uppercase tracking-wide flex-1"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {t('nav.contact')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
